'use client';

import LottieComponent from '@/components/ui/card-with-image/lottie-component';
import { Image } from '@/components/ui/image/image';
import { Media } from '@/components/ui/media/media';
import { cn } from '@/lib/utils';
import { parseMarkdownLinks } from '@/lib/utils/parse-markdown-links';
import type { MediaData } from '@/lib/getMediaData';
import { ChevronDownIcon } from 'lucide-react';
import {
  createContext,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './accordion-feat.module.scss';

type AccordionFeatContextType = {
  openItem: string | null;
  toggleItem: (id: string) => void;
};

export type AccordionFeatItem = {
  id: string;
  title: string;
  description: string;
  icon?: ReactNode;
  image?: string;
  imageAlt?: string;
  imageAspectRatio?: '1:1' | '4:5' | '16:9';
  imageFit?: 'contain' | 'cover';
  mediaData?: MediaData;
  cta?: ReactNode;
};

export type AccordionFeatProps = {
  items: AccordionFeatItem[];
  defaultOpen?: string;
  onItemChange?: (id: string | null) => void;
};

const AccordionFeatContext = createContext<AccordionFeatContextType | null>(null);

const useAccordionFeat = () => {
  const context = useContext(AccordionFeatContext);
  if (!context) throw new Error('useAccordionFeat must be used within an AccordionFeat');
  return context;
};

export const AccordionFeat = ({
  items,
  defaultOpen,
  onItemChange,
  className,
  ...props
}: AccordionFeatProps & HTMLAttributes<HTMLDivElement>) => {
  const [openItem, setOpenItem] = useState<string | null>(defaultOpen ?? items[0]?.id ?? null);

  const toggleItem = (id: string) => {
    if (openItem === id) return;
    setOpenItem(id);
    onItemChange?.(id);
  };

  return (
    <AccordionFeatContext.Provider value={{ openItem, toggleItem }}>
      <div role="presentation" className={cn(styles.component, className)} {...props}>
        {items.map((item) => (
          <AccordionFeatItem key={item.id} item={item} />
        ))}
      </div>
    </AccordionFeatContext.Provider>
  );
};

const AccordionFeatItem = ({ item }: { item: AccordionFeatItem }) => {
  const { openItem, toggleItem } = useAccordionFeat();
  const isOpen = openItem === item.id;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });

      resizeObserver.observe(contentRef.current);
      setContentHeight(contentRef.current.scrollHeight);

      return () => resizeObserver.disconnect();
    }
  }, [item.description]);

  return (
    <>
      <div
        className={cn(styles.component_item, {
          [styles['component_item--expanded']]: isOpen,
        })}
      >
        <button
          id={`accordion-feat-trigger-${item.id}`}
          onClick={() => toggleItem(item.id)}
          aria-expanded={isOpen}
          aria-controls={`accordion-feat-content-${item.id}`}
          className={styles.component_trigger}
        >
          {item.icon && (
            <span className={styles.component_icon} aria-hidden="true">
              {item.icon}
            </span>
          )}
          <span className={styles.component_title}>{item.title}</span>
          <span
            className={cn(styles.component_chevron, {
              [styles['component_chevron--open']]: isOpen,
            })}
            aria-hidden="true"
          >
            <ChevronDownIcon />
          </span>
        </button>
        <div
          id={`accordion-feat-content-${item.id}`}
          className={cn(styles.component_content, {
            [styles['component_content--open']]: isOpen,
          })}
          role="region"
          aria-labelledby={`accordion-feat-trigger-${item.id}`}
          style={{
            maxHeight: isOpen ? `${contentHeight}px` : '0px',
          }}
        >
          <div
            ref={contentRef}
            className={cn(styles.component_content_inner, {
              [styles['component_content_inner--open']]: isOpen,
            })}
          >
            <p>{parseMarkdownLinks(item.description, styles.component_link)}</p>
            {item.cta && <div className={styles.component_cta}>{item.cta}</div>}
            {(item.mediaData || item.image) && (
              <div
                className={styles.component_image}
                style={{
                  aspectRatio: item.imageAspectRatio
                    ? item.imageAspectRatio.replace(':', ' / ')
                    : undefined,
                  overflow: 'hidden',
                }}
              >
                {item.mediaData ? (
                  <Media {...item.mediaData} className="h-full w-full" width={0} height={0} />
                ) : item.image && /\.lottie(\?|$)/i.test(item.image) ? (
                  <LottieComponent src={item.image} className="h-full w-full" />
                ) : item.image ? (
                  <Image
                    src={item.image}
                    alt={item.imageAlt || item.title}
                    width={0}
                    height={0}
                    sizes="100%"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      objectFit: item.imageFit ?? 'cover',
                      objectPosition: 'center',
                      aspectRatio: item.imageAspectRatio
                        ? item.imageAspectRatio.replace(':', ' / ')
                        : undefined,
                    }}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.component_separator} aria-hidden="true" />
    </>
  );
};
